import {
  useAssignmentGetAssignmentsWithNewDataFromRideCountHook,
  useAssignmentGetAssignmentsWithNewDataFromRideHook,
} from 'api/assignment/assignment';
import { AssignmentViewModel } from 'api/model';
import { DefaultWebAdminPolicy, isAuthorized } from 'constants/Roles';
import useAuthenticationState from 'contexts/authentication/useAuthentication';
import useMe from 'contexts/authentication/useMe';
import useUpdateInterval from 'hooks/useUpdateInterval';
import { useCallback, useEffect, useMemo } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';

const UPDATE_INTERVAL = 10 * 1000;

const rideUpdatesAtom = atom<AssignmentViewModel[] | null>({
  key: 'rideUpdatesAtom',
  default: null,
});

const rideUpdatesCountAtom = atom<number>({
  key: 'rideUpdatesCountAtom',
  default: 0,
});

export const useRideUpdatesCountPoll = () => {
  const setRideUpdatesCount = useSetRecoilState(rideUpdatesCountAtom);

  const getAssignmentsWithNewDataFromRideCount =
    useAssignmentGetAssignmentsWithNewDataFromRideCountHook();
  const getUpdatesCountCall = useApiCallFn(() =>
    getAssignmentsWithNewDataFromRideCount()
  );

  const {
    state: { isAuthenticated },
  } = useAuthenticationState();
  const me = useMe();

  const { startIntervalImmediately, stopInterval } = useUpdateInterval(
    async () => {
      const [count] = await getUpdatesCountCall.run();
      if (!Number.isNaN(count) && count !== null) {
        setRideUpdatesCount(count);
      }
    },
    UPDATE_INTERVAL
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      isAuthenticated &&
      !!me &&
      isAuthorized(
        DefaultWebAdminPolicy.allow,
        DefaultWebAdminPolicy.deny,
        me.roles
      )
    ) {
      setTimeout(() => startIntervalImmediately(), 0);
    } else {
      stopInterval();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, me]);
};

const useRideUpdates = () => {
  const [rideUpdates, setRideUpdates] = useRecoilState(rideUpdatesAtom);
  const [rideUpdatesCount, setRideUpdatesCount] =
    useRecoilState(rideUpdatesCountAtom);

  const getAssignmentsWithNewDataFromRide =
    useAssignmentGetAssignmentsWithNewDataFromRideHook();
  const getAssignmentsWithNewDataFromRideCount =
    useAssignmentGetAssignmentsWithNewDataFromRideCountHook();

  const getUpdatesCall = useApiCallFn(() =>
    getAssignmentsWithNewDataFromRide()
  );

  const getUpdatesCountCall = useApiCallFn(() =>
    getAssignmentsWithNewDataFromRideCount()
  );

  const refetchRideUpdates = useCallback(async () => {
    const [updates] = await getUpdatesCall.run();
    const [count] = await getUpdatesCountCall.run();

    if (updates) {
      setRideUpdates(updates);
    }
    if (!Number.isNaN(count) && count !== null) {
      setRideUpdatesCount(count);
    }
  }, [
    getUpdatesCall,
    getUpdatesCountCall,
    setRideUpdates,
    setRideUpdatesCount,
  ]);

  return useMemo(
    () => ({
      loading: getUpdatesCall.status === RequestStatus.Fetching,
      rideUpdates,
      rideUpdatesCount,
      refetchRideUpdates,
    }),
    [getUpdatesCall.status, refetchRideUpdates, rideUpdates, rideUpdatesCount]
  );
};

export default useRideUpdates;
